/*
 * Каталог промптов саммари. Два уровня доступа:
 *  - GET /summary-profiles — лёгкий список активных профилей (slug/label/hint)
 *    для пикеров; доступен любой веб-роли. Заменяет прежний хардкод во фронте.
 *  - /prompts* — admin-only управление каталогом (полный детейл + CRUD + общие
 *    части). Гейт на бэке (requireAdminRole); фронт дополнительно прячет UI.
 */
import { apiFetch, apiGetJSON, ApiError } from "./client";

/** Лёгкая мета профиля для пикеров (без тел промптов). */
export interface SummaryProfileMeta {
  slug: string;
  label: string;
  hint: string;
}

export interface SummaryProfilesResponse {
  profiles: SummaryProfileMeta[];
  default: string;
}

/** GET /api/v1/summary-profiles — активные профили для пикера. */
export async function fetchSummaryProfiles(): Promise<SummaryProfilesResponse> {
  return apiGetJSON<SummaryProfilesResponse>("/v1/summary-profiles");
}

// ─── admin ─────────────────────────────────────────────────────────────────

/** Полный редактируемый профиль (admin). */
export interface PromptProfile {
  slug: string;
  label: string;
  hint: string;
  intro: string;
  sections: string;
  common_slug: string;
  sort_order: number;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

/** Общая часть промпта (блок 2). */
export interface PromptCommon {
  slug: string;
  label: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPromptsResponse {
  profiles: PromptProfile[];
  commons: PromptCommon[];
  /** Закрытый блок «Формат ответа» — read-only, показываем в редакторе. */
  format_header: string;
}

/** Поля для создания/обновления профиля. slug нужен только при создании. */
export interface ProfileInput {
  slug?: string;
  label: string;
  hint: string;
  intro: string;
  sections: string;
  common_slug: string;
  sort_order: number;
}

/** GET /api/v1/prompts — полный каталог для admin-редактора. */
export async function fetchPrompts(): Promise<AdminPromptsResponse> {
  return apiGetJSON<AdminPromptsResponse>("/v1/prompts");
}

/** POST /api/v1/prompts/profiles — создать профиль. */
export async function createProfile(body: ProfileInput): Promise<PromptProfile> {
  const res = await apiFetch("/v1/prompts/profiles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `POST profile ${res.status}: ${text}`);
  }
  return (await res.json()) as PromptProfile;
}

/** PUT /api/v1/prompts/profiles/{slug} — обновить профиль (slug иммутабелен). */
export async function updateProfile(
  slug: string,
  body: ProfileInput,
): Promise<PromptProfile> {
  const res = await apiFetch(`/v1/prompts/profiles/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `PUT profile ${slug} ${res.status}: ${text}`);
  }
  return (await res.json()) as PromptProfile;
}

/** POST /api/v1/prompts/profiles/{slug}/archive — soft-delete профиля. */
export async function archiveProfile(slug: string): Promise<void> {
  const res = await apiFetch(
    `/v1/prompts/profiles/${encodeURIComponent(slug)}/archive`,
    { method: "POST" },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `archive profile ${slug} ${res.status}: ${text}`);
  }
}

/** PUT /api/v1/prompts/commons/{slug} — обновить общую часть. */
export async function updateCommon(
  slug: string,
  body: { label: string; body: string },
): Promise<PromptCommon> {
  const res = await apiFetch(`/v1/prompts/commons/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `PUT common ${slug} ${res.status}: ${text}`);
  }
  return (await res.json()) as PromptCommon;
}
